import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import { PagedProspects, Prospect, ProspectStatus } from 'core/application/ichraDecisionTool/dashboard/getProspects'

import Icon from 'common/components/general/Icon'

import { Dropdown, Table, TableColumns } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'

import Actions from './Actions'
import InternalUserStatusIcon from './ProspectStatusIcon'

import './ProspectsTable.less'

interface Props {
  setSearchFilter: (value: OrderedPageFilter) => void
  searchFilter: OrderedPageFilter
  prospects: PagedProspects
  loadingProspects: boolean
  history: any
  translate: (key: string) => string
  onEdit: (id: string) => void
  onStartProposal: (id: string, companyName: string) => void
  onUploadEmployeeCensus: (id: string, companyName: string) => void
}

const ProspectsTable = ({
  setSearchFilter,
  searchFilter,
  prospects,
  loadingProspects,
  history,
  translate,
  onEdit,
  onStartProposal,
  onUploadEmployeeCensus,
}: Props) => {
  const getColumns = (filter: OrderedPageFilter): TableColumns =>
    [
      {
        title: translate('prospects-table.company-name'),
        dataIndex: 'companyName',
        key: 'companyName',
        sorter: true,
        render: (_, { companyName }: Prospect) => (
          <span>
            <p>{companyName}</p>
          </span>
        ),
      },
      {
        title: translate('prospects-table.p-first-name'),
        dataIndex: 'firstName',
        key: 'firstName',
        sorter: true,
        render: (_, { firstName }: Prospect) => (
          <span>
            <p>{firstName}</p>
          </span>
        ),
      },
      {
        title: translate('prospects-table.p-last-name'),
        dataIndex: 'lastName',
        key: 'lastName',
        sorter: true,
        render: (_, { lastName }: Prospect) => (
          <span>
            <p>{lastName}</p>
          </span>
        ),
      },
      {
        title: translate('prospects-table.email'),
        dataIndex: 'email',
        key: 'email',
        sorter: true,
        render: (_, { email }: Prospect) => {
          return (
            <span>
              <p>{email}</p>
            </span>
          )
        },
      },
      {
        title: translate('prospects-table.created-by'),
        dataIndex: 'createdByPba',
        key: 'createdByPba',
        sorter: true,
        render: (_, { createdByPba }: Prospect) => (
          <span>
            <p>{createdByPba}</p>
          </span>
        ),
      },
      {
        title: translate('prospects-table.date-created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        render: (_, { createdAt }: Prospect) => (
          <span>
            <p>{parseZoneFormat(createdAt)}</p>
          </span>
        ),
      },
      {
        title: translate('prospects-table.status'),
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        render: (_, { status }: Prospect) => (
          <span>
            <p>
              <InternalUserStatusIcon status={+status} />
            </p>
          </span>
        ),
      },
      {
        title: translate('prospects-table.actions'),
        dataIndex: '',
        key: '',
        sorter: false,
        render: (_, { id, companyName, status }: Prospect) => (
          <span>
            <div className="actions">
              <Dropdown
                overlay={
                  <Actions
                    translate={translate}
                    editDisabled={calculateEditDisabled(status)}
                    startProposalDisabled={calculateStartProposalDisabled(status)}
                    uploadEmployeeCensusDisabled={calculateUploadEmployeeCensusDisabled(status)}
                    onEdit={() => onEdit(id)}
                    onStartProposal={() => onStartProposal(id, companyName)}
                    onUploadEmployeeCensus={() => onUploadEmployeeCensus(id, companyName)}
                  />
                }
                placement="bottomCenter"
              >
                <div className="prospect_actions">
                  <Icon type="far fa-ellipsis-h" fontAwesome />
                </div>
              </Dropdown>
            </div>
          </span>
        ),
      },
    ].map((x) =>
      x.key === filter.orderColumn ? { ...x, ['defaultSortOrder']: filter.orderDescending ? 'descend' : 'ascend' } : x,
    )

  const onExpensesTableChange = (pagination: any, _, sorter: any) => {
    setSearchFilter({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  const calculateStartProposalDisabled = (status: ProspectStatus) =>
    !(status === ProspectStatus.Completed || status === ProspectStatus.ProposalSent)

  const calculateEditDisabled = (status: ProspectStatus) =>
    !(status === ProspectStatus.InProgress || status === ProspectStatus.New)

  const calculateUploadEmployeeCensusDisabled = (status: ProspectStatus) =>
    !(status === ProspectStatus.New || status === ProspectStatus.InProgress)

  return (
    <Table
      dataSource={prospects.items}
      onChange={onExpensesTableChange}
      loading={loadingProspects}
      columns={getColumns(searchFilter)}
      pagination={{
        total: prospects.totalItemCount,
        pageSize: searchFilter.pageSize,
        current: prospects.pageNumber,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30', '40'],
      }}
    />
  )
}

export default ProspectsTable
